import { calculateXirr } from './internal-rate-of-return/calculateXirr';

/**
 * Investment analysis results
 */
interface InvestmentAnalysis {
  investmentNPV: number;
  totalReturns: number;
  cagr: number;
  internalRateOfReturn: number | undefined;
  yearsToLastReturn: number;
}

/**
 * Sensitivity analysis result for different discount rates
 */
interface SensitivityResult {
  discountRate: number;
  cagr: number;
}

/**
 * Calculate NPV of investment payments
 */
export function calculateInvestmentNPV(
  investments: CashFlow[],
  discountRate: number,
): number {
  return investments.reduce((npv, investment) => {
    return (
      npv + investment.amount / Math.pow(1 + discountRate, investment.year)
    );
  }, 0);
}

/**
 * Calculate IRR for investment cash flows using XIRR
 * Uses the new calculateXirr function with date-based cash flows
 */
function calculateInvestmentIRR(
  investments: CashFlow[],
  returns: CashFlow[],
  discountRate: number,
  baseYear?: number,
): number | undefined {
  if (investments.length === 0 && returns.length === 0) {
    return 0;
  }

  const effectiveBaseYear = baseYear ?? new Date().getFullYear();

  try {
    // Convert year-based cash flows to date-based cash flows for XIRR
    const xirrCashFlows: Array<{ amount: number; date: Date }> = [];

    // Add investments as negative cash flows
    investments.forEach(investment => {
      xirrCashFlows.push({
        amount: -(investment.amount ?? 0), // Negative for outflows
        date: new Date(effectiveBaseYear + investment.year, 0, 1),
      });
    });

    // Add returns as positive cash flows
    returns.forEach(returnFlow => {
      xirrCashFlows.push({
        amount: returnFlow.amount ?? 0, // Positive for inflows
        date: new Date(effectiveBaseYear + returnFlow.year, 0, 1),
      });
    });

    // Calculate XIRR
    const xirr = calculateXirr(xirrCashFlows, {
      initialGuess: 0.1,
      tolerance: 1e-10,
      maxIterations: 100,
      useBracketing: true,
    });

    return xirr;
  } catch (error) {
    // If XIRR calculation fails, try with lumped investments using NPV
    try {
      const npvInvestments = calculateInvestmentNPV(investments, discountRate);
      const xirrCashFlows: Array<{ amount: number; date: Date }> = [];

      // Use NPV as single investment at year 0
      xirrCashFlows.push({
        amount: -npvInvestments, // Negative for outflow
        date: new Date(effectiveBaseYear, 0, 1),
      });

      // Add returns as positive cash flows
      returns.forEach(returnFlow => {
        xirrCashFlows.push({
          amount: returnFlow.amount ?? 0,
          date: new Date(effectiveBaseYear + returnFlow.year, 0, 1),
        });
      });

      const xirr = calculateXirr(xirrCashFlows, {
        initialGuess: 0.1,
        tolerance: 1e-10,
        maxIterations: 100,
        useBracketing: true,
      });

      return xirr;
    } catch (fallbackError) {
      // If both attempts fail, return undefined
      console.warn('XIRR calculation failed:', fallbackError);
      return undefined;
    }
  }
}

/**
 * Analyze investment returns
 */
export function analyzeInvestmentReturns(
  investments: CashFlow[],
  returns: CashFlow[],
  discountRateWholeNumber: number = 0,
  baseYear?: number,
  calculateIRR: boolean = true,
): InvestmentAnalysis {
  const discountRate = discountRateWholeNumber / 100;
  const investmentNPV = calculateInvestmentNPV(investments, discountRate);
  const totalReturns = returns.reduce((sum, r) => sum + r.amount, 0);
  const yearsToLastReturn = Math.max(...returns.map(r => r.year)) + 1; // +1 because we're using 0-indexed array

  let IRR = undefined;

  if (investmentNPV <= 0) {
    return {
      investmentNPV: 0,
      totalReturns,
      cagr: Infinity,
      internalRateOfReturn: calculateIRR ? Infinity : undefined,
      yearsToLastReturn,
    };
  }

  const cagr = Math.pow(totalReturns / investmentNPV, 1 / yearsToLastReturn) - 1;

  if (calculateIRR) {
    IRR = calculateInvestmentIRR(investments, returns, discountRate, baseYear);
  }

  return {
    investmentNPV,
    totalReturns,
    cagr,
    internalRateOfReturn: IRR,
    yearsToLastReturn,
  };
}

/**
 * Perform sensitivity analysis
 */
export function performSensitivityAnalysis(
  investments: CashFlow[],
  returns: CashFlow[],
  discountRates: number[],
): SensitivityResult[] {
  const yearsToLastReturn = Math.max(...returns.map(r => r.year));
  const totalReturns = returns.reduce((sum, r) => sum + r.amount, 0);

  return discountRates.map(rate => {
    const npv = calculateInvestmentNPV(investments, rate);
    const cagr = Math.pow(totalReturns / npv, 1 / yearsToLastReturn) - 1;

    return {
      discountRate: rate,
      cagr,
    };
  });
}
